/**
 * Copyright (c) 2022 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useContext, useEffect, useState } from 'react';
import { isEqual } from 'lodash';

import { LinkButton, Text } from '@blend/enterprise-components';

import { MeContext } from 'blb-contexts/Me';
import { BlendBridge } from 'blb-types/blendBridge';
import { FormError } from 'blb-components/FormError';
import { FieldArray } from 'react-final-form-arrays';
import { FormApi } from 'final-form';
import { AddMemberButtonWrapper, MemberFields } from '../styles';
import { CreateTeamWithMembersFormValues } from './CreateTeamModal';
import { Row, InviteMemberRow } from './InviteTeamMemberRow';

const getEmptyMember = (principalType: BlendBridge.PrincipalType) => ({
  isAdmin: false,
  isBroadcaster: false,
  principalType,
  username: '',
});

const getBroadcasterMember = (username: string, principalType: BlendBridge.PrincipalType) => ({
  isAdmin: false,
  isBroadcaster: true,
  principalType,
  username,
  disabled: true,
  required: true,
});

const getAllEmptyIndices = (members: MemberAssignment[]): number[] =>
  members
    .map((m: MemberAssignment, index: number) => {
      if (m.username === '') {
        return index;
      }
    })
    .filter(Boolean) as number[];

const hasBroadcaster = (members: MemberAssignment[]) => members.some(member => member.isBroadcaster);

/**
 * This function is used to handle cases when a user first selected one
 * shareType and then went back and changed it. At a high level, we only want to
 * update the current user and broadcaster user (if present).
 *
 * @param formMutators - a set of functions available to update form data
 * @param stateMembers - the current set of members
 * @param formMembers - previous set of members / what final-form knows about
 * the members
 */
const reconcileFormUpdates = (
  formMutators: FormApi['mutators'],
  stateMembers: MemberAssignment[],
  formMembers: MemberAssignment[],
) => {
  const referenceArray: MemberAssignment[] = [...formMembers];
  // Check if the indices and values of the stateMembers matches those of the
  // formMembers, and if not, overwrite the formMember with the stateMember
  stateMembers.forEach((stateMember: MemberAssignment, index: number) => {
    if (!isEqual(formMembers[index], stateMember) && stateMember.username !== '') {
      referenceArray[index] = stateMember;
      formMutators.update('members', index, stateMember);
    }
  });

  // If the old/formMembers have a broadcaster but the new/stateMembers don't
  // update them to not be a broadcaser anymore
  if (!hasBroadcaster(stateMembers) && hasBroadcaster(formMembers)) {
    const broadcasterIndex = formMembers.findIndex(member => member.isBroadcaster);
    const isCurrentUser = broadcasterIndex === 0;
    const noLongerBroadcasterUser = {
      ...formMembers[broadcasterIndex],
      isBroadcaster: false,
      disabled: isCurrentUser,
      required: isCurrentUser,
    };
    formMutators.update('members', broadcasterIndex, noLongerBroadcasterUser);
    referenceArray[broadcasterIndex] = noLongerBroadcasterUser;
  }

  // get indices of all empty members and remove all but one
  const emptyMembersIdxs = getAllEmptyIndices(referenceArray);
  const emptyMemberExistsAtEnd =
    !!emptyMembersIdxs.length && emptyMembersIdxs.find(idx => idx === referenceArray.length - 1);

  if (!emptyMemberExistsAtEnd) {
    formMutators.removeBatch('members', emptyMembersIdxs);
  } else if (emptyMembersIdxs.length > 1) {
    emptyMembersIdxs.pop(); // remove the last one so that we still have 1 empty member
    formMutators.removeBatch('members', emptyMembersIdxs);
  }
};

const getInitialMembers = (
  principalType: BlendBridge.PrincipalType,
  broadcasterEmail?: string,
  currentUserEmail?: string,
) => {
  const isCurrentUserAlsoBroadcaster = broadcasterEmail === currentUserEmail;
  const initialMembers = [];
  if (!!currentUserEmail) {
    const teamCreatorMember = {
      isAdmin: true,
      isBroadcaster: isCurrentUserAlsoBroadcaster,
      principalType,
      username: currentUserEmail,
      disabled: true,
      required: true,
    };
    initialMembers.push(teamCreatorMember);
  }
  if (!!broadcasterEmail && !isCurrentUserAlsoBroadcaster) {
    const broadcasterMember = getBroadcasterMember(broadcasterEmail, principalType);
    initialMembers.push(broadcasterMember);
  }
  initialMembers.push(getEmptyMember(principalType));

  return initialMembers;
};

const getMemberRowKey = (member: MemberAssignment, index: number) =>
  member.isBroadcaster ? 'broadcaster-row' : `member-${index}-row`;

export interface MemberAssignment {
  isAdmin: boolean;
  isBroadcaster?: boolean;
  principalType: BlendBridge.PrincipalType;
  username: string;
}

export interface MembersFormValues {
  members: MemberAssignment[];
}

interface InviteTeamMembersProps {
  broadcasterEmail?: string;
  currentUserEmail?: string;
  form: FormApi<CreateTeamWithMembersFormValues | MembersFormValues>;
}

export const InviteTeamMembers: React.FC<InviteTeamMembersProps> = ({ broadcasterEmail, currentUserEmail, form }) => {
  const me = useContext(MeContext)!;
  const [members] = useState(getInitialMembers(me.principalType, broadcasterEmail, currentUserEmail));
  const [formMembers] = useState(form.getState().values.members || []);

  useEffect(() => {
    reconcileFormUpdates(form.mutators, members, formMembers);
  }, [formMembers, form.mutators, me.principalType, members]);

  return (
    <>
      <Text fontSize="md" weight="semibold">
        Admin
      </Text>
      <Text fontSize="md">
        Admins have the ability to edit team information, add/remove team members and admins, and delete the team.
      </Text>
      <FieldArray initialValue={members} name="members">
        {({ fields }) => (
          <MemberFields>
            <Row>
              <Text fontSize="sm" weight="semibold">
                Username
              </Text>
              <Text fontSize="sm" weight="semibold">
                Admin
              </Text>
            </Row>

            {fields.map((_name, index) => {
              const member = fields.value[index];
              return (
                <InviteMemberRow
                  index={index}
                  key={getMemberRowKey(member as MemberAssignment, index)}
                  member={member}
                  remove={idx => fields.remove(idx)}
                />
              );
            })}
          </MemberFields>
        )}
      </FieldArray>
      <FormError name="members" />
      <AddMemberButtonWrapper>
        <LinkButton
          btnSize="sm"
          id="it-addmembers-btn"
          label="Add team member"
          onClick={() => void form.mutators.push('members', getEmptyMember(me.principalType))}
        />
      </AddMemberButtonWrapper>
    </>
  );
};
